import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const GatepassPage = () => {
  const storedSessionId = localStorage.getItem("currentSessionId");
  const [selectedSession] = useState(storedSessionId || "");

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [studentDetails, setStudentDetails] = useState(null);
  const [guardians, setGuardians] = useState([]);
  const [selectedGuardian, setSelectedGuardian] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newGuardian, setNewGuardian] = useState({ name: "", phone: "", relation: "", image: null });
  const [gatepasses, setGatepasses] = useState([]);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const labelStyle = "text-black font-semibold !text-[18px] mb-1";
  const inputStyle = "p-2 border rounded w-full text-[14px] placeholder:text-[14px] placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500";

  // Fetch school info
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/school/getSchool`)
      .then(res => setSchoolInfo(res.data[0]))
      .catch(err => console.error(err));
  }, []);

  // Fetch gatepasses
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/gatepasses/getGatepasses`)
      .then(res => setGatepasses(res.data))
      .catch(err => console.error(err));
  }, []);

  // Fetch classes based on selected session
  useEffect(() => {
    if (!selectedSession) return;

    axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${selectedSession}`)
      .then(res => setClasses(res.data))
      .catch(err => console.error(err));

    setSelectedClass("");
    setSections([]);
    setSelectedSection("");
  }, [selectedSession]);

  // Fetch sections when selected class changes
  useEffect(() => {
    if (!selectedClass || !selectedSession) {
      setSections([]);
      setSelectedSection("");
      return;
    }

    axios.get(` ${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClass)}/getSectionsByClassName?sessionId=${selectedSession}`)
      .then(res => setSections(Array.isArray(res.data) ? res.data : res.data.sections || []))
      .catch(err => {
        console.error("Failed to fetch sections:", err);
        setSections([]);
      });

    setSelectedSection("");
  }, [selectedClass, selectedSession]);

  // Fetch students when class + section + session are selected
  useEffect(() => {
    if (!selectedClass || !selectedSection || !selectedSession) {
      setStudents([]);
      setStudentDetails(null);
      setGuardians([]);
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
        params: {
          class: selectedClass,
          section: selectedSection,
          sessionId: selectedSession,
          status: "aAtive",
        },
      })
      .then((res) => setStudents(res.data))
      .catch((err) => console.error("Error fetching students:", err));
  }, [selectedClass, selectedSection, selectedSession]);

  // Fetch selected student details + guardians
  useEffect(() => {
    if (!selectedStudent) {
      setStudentDetails(null);
      setGuardians([]);
      return;
    }

    // Fetch student details
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, {
        params: {
          class: selectedClass,
          section: selectedSection,
          sessionId: selectedSession,
          status: "active",
        },
      })
      .then((res) => {
        const student = res.data.find((student) => student._id === selectedStudent);
        setStudentDetails(student || null);
      })
      .catch(() => {
        setStudentDetails(null);
      });

    // Fetch guardians for the selected student
    axios
      .get(` ${process.env.REACT_APP_API_BASE_URL}/api/guardians/getGuardiansByStudentId/${selectedStudent}`)
      .then((res) => {
        setGuardians(res.data.guardians || []);
      })
      .catch(() => {
        setGuardians([]);
      });
  }, [selectedStudent, selectedClass, selectedSection, selectedSession]);



  const handleGuardianSelection = (person) => setSelectedGuardian(person);
  const handleInputChange = (e) => setNewGuardian({ ...newGuardian, [e.target.name]: e.target.value });
  const handleAddGuardian = () => {
    setGuardians([...guardians, newGuardian]);
    setShowModal(false);
  };
  const handleDelete = async (gatepassId) => {
    if (!window.confirm("Are you sure you want to delete this gatepass?")) return;

    try {
      const storedSessionId = localStorage.getItem("currentSessionId");

      // Delete the gatepass by ID
      await axios.delete(` ${process.env.REACT_APP_API_BASE_URL}/api/gatepasses/deleteGatepass/${gatepassId}`, {
        params: { sessionId: storedSessionId },
      });

      // Remove the deleted gatepass from the state
      setGatepasses((prevGatepasses) =>
        prevGatepasses.filter((gp) => gp._id !== gatepassId)
      );

      alert("Gatepass deleted successfully!");
    } catch (error) {
      alert("Failed to delete gatepass.");
      console.error("Error deleting gatepass:", error);
    }
  };

  const handleSubmit = async () => {
    if (!selectedGuardian) return alert("Please select a guardian before submitting.");
    if (!studentDetails) return alert("Select a student before submitting.");

    const childList = [{
      _id: studentDetails._id,
      name: `${studentDetails.firstName} ${studentDetails.lastName}`,
      class: studentDetails.class,
      section: studentDetails.section
    }];

    try {
      setLoading(true);
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/gatepasses/createGatepass`, {
        guardian: selectedGuardian,
        children: childList
      });
      alert("Gatepass created successfully!");
      window.location.reload();
    } catch (error) {
      console.error(error);
      alert("Failed to submit gatepass.");
    } finally {
      setLoading(false);
    }
  };

  const handleView = (id) => navigate(`/view-gatepass/${id}`);

  return (
    <div className="max-w-screen mx-auto p-4 md:p-6 bg-white shadow rounded-lg">

      {/* CLASS / SECTION / STUDENT SELECT */}
      <div className="mb-8">
        <h2 className={`${labelStyle} mb-3`}>Search by Student</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">

          <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} className={inputStyle}>
            <option value="">Select Class</option>
            {classes.map(c => <option key={c._id} value={c.className}>{c.className}</option>)}
          </select>

          <select value={selectedSection} onChange={e => setSelectedSection(e.target.value)} className={inputStyle} disabled={!selectedClass}>
            <option value="">Select Section</option>
            {sections.map(s => <option key={s._id} value={s.sectionName}>{s.sectionName}</option>)}
          </select>

          <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)} className={inputStyle} disabled={!selectedSection}>
            <option value="">Select Student</option>
            {students.map(s => <option key={s._id} value={s._id}>{s.firstName} {s.lastName}</option>)}
          </select>


        </div>
      </div>

      {/* GUARDIAN TABLE */}
      {guardians.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <h3 className={`${labelStyle} mb-2`}>Guardian Details</h3>
          <table className="min-w-full border-collapse border border-gray-300 text-center">
            <thead className="bg-gray-200">
              <tr>
                {["Select", "Image", "Name", "Phone", "Relation"].map(title => <th key={title} className="border px-2 py-1 text-black !text-[18px] font-semibold">{title}</th>)}
              </tr>
            </thead>
            <tbody>
              {guardians.map((g, idx) => (
                <tr key={idx} className="hover:bg-gray-100">
                  <td className="border px-2 py-1">
                    <input type="checkbox" onChange={() => handleGuardianSelection(g)} checked={selectedGuardian?.name === g.name} />
                  </td>
                  <td className="border px-2 py-1">
                    <img src={g.image || "https://via.placeholder.com/40"} className="w-10 h-10 rounded-full mx-auto" />
                  </td>
                  <td className="border px-2 py-1 text-[14px]">{g.name}</td>
                  <td className="border px-2 py-1 text-[14px]">{g.phone}</td>
                  <td className="border px-2 py-1 text-[14px]">{g.relation}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button onClick={() => setShowModal(true)} className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-500">
            Add Guardian
          </button>
        </div>
      )}

      {/* SUBMIT BUTTON */}
      <button onClick={handleSubmit} disabled={loading} className="mt-6 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded w-full md:w-auto">
        {loading ? "Submitting..." : "Submit"}
      </button>

      {/* ADD GUARDIAN MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-11/12 sm:w-96 shadow-xl">
            <h3 className={`${labelStyle} mb-4`}>Add Guardian</h3>
            <div className="space-y-4">
              <input type="text" name="name" placeholder="Name" value={newGuardian.name} onChange={handleInputChange} className={inputStyle} />
              <input type="text" name="phone" placeholder="Phone" value={newGuardian.phone} onChange={handleInputChange} className={inputStyle} />
              <input type="text" name="relation" placeholder="Relation" value={newGuardian.relation} onChange={handleInputChange} className={inputStyle} />

              <div className="flex justify-end gap-4">
                <button onClick={handleAddGuardian} className="px-4 py-2 bg-blue-600 text-white rounded">Add</button>
                <button onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-400 rounded">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GATEPASS TABLE */}
      <div className="mt-10 overflow-x-auto">
        <h2 className="text-center !text-[18px] font-bold mb-4 text-black">Gatepass Table</h2>
        <table className="min-w-full border-collapse border border-gray-300 text-center">
          <thead className="bg-gray-200">
            <tr>
              {["Gatepass ID", "Guardian Name", "Relation", "Phone", "Action", "Action"].map(title => <th key={title} className="border px-2 py-1 text-black !text-[18px] font-semibold">{title}</th>)}
            </tr>
          </thead>
          <tbody>
            {gatepasses.map(gp => (
              <tr key={gp._id} className="hover:bg-gray-100">
                <td className="border px-2 py-1 text-[14px]">{gp.gatepassID}</td>
                <td className="border px-2 py-1 text-[14px]">{gp.guardian.name}</td>
                <td className="border px-2 py-1 text-[14px]">{gp.guardian.relation}</td>
                <td className="border px-2 py-1 text-[14px]">{gp.guardian.phone}</td>
                <td className="border px-2 py-1">
                  <button onClick={() => handleView(gp._id)} className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-500">View</button>
                </td>
                <td className="border px-2 py-1">
                  <td className="border px-2 py-1">
                    <button
                      onClick={() => handleDelete(gp._id)} // Pass the gatepass ID
                      className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-500"
                    >
                      Delete
                    </button>
                  </td>


                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default GatepassPage;
